// Plan 面板渲染 / Plan panel rendering + Markdown upload handling.
// 端口自 legacy app.js 的 parsePlan / renderPlanPreview / readPlanFile 等 13 个函数。
// Ports 13 legacy plan-panel functions: parse / preview / empty / status / title / summary /
// title-extract / markdown build / upload / filename-normalize / input-clear / markdown lookup.

import { $maybe, escapeHtml } from "@/lib/dom";
import { log } from "@/lib/logging";
import {
  renderMarkdownToSafeHtml,
  stripMarkdownFrontmatter,
} from "@/lib/markdown";
import { plansApi as realPlansApi } from "@/api/plans";
import { state } from "@/state/store";
import type { PlanRef } from "@/lib/types";
import { appendLog } from "@/features/log-stream";

// 最大允许的 Markdown 文件体积 / Maximum allowed Markdown file size.
export const MAX_MARKDOWN_BYTES = 2 * 1024 * 1024;

// 插槽类型别名 / Plan slot alias.
export type Slot = "A" | "B";

// 后端解析出的 plan summary（保持 Record<string, unknown> 宽松契约）
// Parsed plan summary from backend; kept as a loose record.
export type PlanSummary = Record<string, unknown> | null | undefined;

// Plan Markdown 预览 HTML 参数 / Plan Markdown preview HTML options.
export interface PlanPreviewHtmlOptions {
  slot?: Slot;
  plan?: PlanSummary | { filename?: string };
  filename?: string;
  fallbackTitle?: string;
  emptyMessage?: string;
}

type PlansApiShape = Pick<typeof realPlansApi, "parse">;

/**
 * 解析 PlanRef 并同步预览 / Parse a PlanRef and update the preview.
 */
export async function parsePlan(
  ref: PlanRef,
  opts: { plansApi?: PlansApiShape } = {},
): Promise<Record<string, unknown>> {
  const plansApi = opts.plansApi ?? realPlansApi;
  const slot = inferSlot(ref);
  try {
    const body = await plansApi.parse(ref);
    const plan = body.plan as Record<string, unknown>;
    const fallbackTitle =
      String(plan["title"] ?? plan["product_name"] ?? plan["filename"] ?? `Plan ${slot}`);
    setPlanSummary(slot, plan, fallbackTitle, ref.source, getMarkdownForRef(ref));
    appendLog(
      `parsed plan ${slot}: ${plan["product_name"] ?? plan["title"] ?? plan["filename"] ?? ""}`,
    );
    return plan;
  } catch (error) {
    setPlanStatus(slot, "error", "error");
    const message = error instanceof Error ? error.message : String(error);
    appendLog(`parse plan ${slot} failed: ${message}`);
    throw error;
  }
}

/**
 * 渲染空态 Plan 面板 / Render the empty-state Plan panel.
 */
export function renderPlanEmpty(slot: Slot, message: string): void {
  const node = $maybe(slot === "A" ? "plan-preview-a" : "plan-preview-b");
  if (!node) return;
  node.innerHTML = renderPlanEmptyHtml(
    slot,
    message || "Load a built-in demo, upload Markdown, or paste plan text.",
  );
}

/**
 * 构建可复用 Plan Markdown 预览 HTML / Build reusable Plan Markdown preview HTML.
 */
export function renderPlanMarkdownPreviewHtml(
  markdown: string,
  options: PlanPreviewHtmlOptions = {},
): string {
  const slot = options.slot ?? "A";
  const planRecord = (options.plan ?? {}) as Record<string, unknown>;
  const source = stripMarkdownFrontmatter(
    markdown ||
      buildPlanMarkdownFromSummary(planRecord, options.fallbackTitle ?? `Plan ${slot}`),
  ).trim();
  if (!source) {
    return renderPlanEmptyHtml(slot, options.emptyMessage ?? "No readable Markdown content.");
  }
  const planFilename = typeof planRecord["filename"] === "string"
    ? planRecord["filename"]
    : "";
  const filename = options.filename || planFilename || `plan-${slot.toLowerCase()}.md`;
  return `
    <div class="plan-preview-scroll">
      <div class="plan-preview-kicker">${escapeHtml(filename.toUpperCase())}</div>
      <div class="plan-markdown">${renderMarkdownToSafeHtml(source, { stripFrontmatter: true })}</div>
    </div>
  `;
}

/**
 * 渲染带预览内容的 Plan 面板 / Render a populated Plan panel preview.
 */
export function renderPlanPreview(
  slot: Slot,
  markdown: string,
  plan: PlanSummary | { filename?: string } = null,
): void {
  const node = $maybe(slot === "A" ? "plan-preview-a" : "plan-preview-b");
  if (!node) return;

  const filename = previewFilename(plan, slot);
  node.innerHTML = renderPlanMarkdownPreviewHtml(markdown, {
    slot,
    plan,
    ...(filename ? { filename } : {}),
  });
}

// 预览文件名回退 / Resolve the preview filename fallback.
function previewFilename(plan: PlanSummary | { filename?: string }, slot: Slot): string | undefined {
  const planRecord = (plan ?? {}) as Record<string, unknown>;
  return typeof planRecord["filename"] === "string"
    ? undefined
    : state.markdown[slot].filename;
}

// 构建空态预览 HTML / Build empty-state preview HTML.
function renderPlanEmptyHtml(slot: Slot, message: string): string {
  return `
    <div class="plan-preview-scroll">
      <div class="plan-empty">
        <div class="plan-preview-kicker">PLAN · ${escapeHtml(slot)}</div>
        <strong>No plan loaded</strong>
        <span>${escapeHtml(message)}</span>
      </div>
    </div>
  `;
}

/**
 * 设置 status-pill 文本与样式 / Set the Plan status-pill text + class.
 */
export function setPlanStatus(
  slot: Slot,
  className: "ready" | "error" | "empty" | string,
  text: string,
): void {
  const node = $maybe(slot === "A" ? "plan-a-status" : "plan-b-status");
  if (!node) return;
  const suffix = className === "ready" || className === "error" ? className : "";
  node.className = `status-pill ${suffix}`.trim();
  node.textContent = text;
}

/**
 * 设置 Plan 标题（面板 header）/ Set the Plan header title.
 */
export function setPlanTitle(slot: Slot, title: string): void {
  const node = $maybe(slot === "A" ? "plan-a-title" : "plan-b-title");
  if (node) node.textContent = title;
}

/**
 * 根据 parsed plan + 原 Markdown 渲染 Plan 预览的上层入口
 * Wrapper that fills status/title/preview from a parsed plan + raw markdown.
 */
export function setPlanSummary(
  slot: Slot,
  plan: PlanSummary,
  title: string,
  source: string,
  markdown: string,
): void {
  setPlanTitle(slot, title || `Plan ${slot}`);
  setPlanStatus(slot, "ready", source || "ready");
  if (!plan) {
    if (markdown) {
      renderPlanPreview(slot, markdown, { filename: `plan-${slot.toLowerCase()}.md` });
    }
    return;
  }
  renderPlanPreview(slot, markdown || buildPlanMarkdownFromSummary(plan, title), plan);
}

/**
 * 从 Markdown 里抽取首个 # 一级标题 / Extract the first H1 title from Markdown.
 */
export function extractMarkdownTitle(markdown: string): string {
  const source = stripMarkdownFrontmatter(markdown);
  const match = source.match(/^#\s+(.+?)\s*$/m);
  return match && match[1] ? match[1].trim() : "";
}

/**
 * 由 plan summary 构造展示用 Markdown / Build fallback Markdown from a parsed summary.
 */
export function buildPlanMarkdownFromSummary(
  plan: Record<string, unknown> | null | undefined,
  fallbackTitle: string,
): string {
  if (!plan) return "";
  const lines: string[] = [];
  const title = String(
    plan["title"] ?? plan["product_name"] ?? fallbackTitle ?? "Untitled plan",
  );
  lines.push(`# ${title}`);
  if (plan["description"]) {
    lines.push(String(plan["description"]));
  }
  if (plan["positioning"]) {
    lines.push(`## Positioning\n\n${String(plan["positioning"])}`);
  }
  const diffs = plan["differentiators"];
  if (Array.isArray(diffs) && diffs.length) {
    lines.push(`## Differentiated Value\n\n${diffs.map((item) => `- ${String(item)}`).join("\n")}`);
  }
  if (plan["validation_question"]) {
    lines.push(`## Validation Question\n\n${String(plan["validation_question"])}`);
  }
  return lines.join("\n\n");
}

/**
 * 把当前插槽切换为 "上传 Markdown" 模式并更新 UI / Mark the slot as a Markdown-uploaded plan.
 */
export function markMarkdownPlan(slot: Slot): void {
  const markdown = state.markdown[slot].content;
  if (markdown.trim()) {
    setPlanStatus(slot, "ready", "markdown");
    setPlanTitle(slot, extractMarkdownTitle(markdown) || `Uploaded Markdown ${slot}`);
    renderPlanPreview(slot, markdown, { filename: state.markdown[slot].filename });
    if (slot === "A") {
      state.planA = null;
    } else {
      state.planB = null;
    }
  } else {
    setPlanStatus(slot, "empty", "empty");
    setPlanTitle(slot, "No plan selected");
    renderPlanEmpty(
      slot,
      slot === "A" ? "Waiting for Markdown or a built-in demo." : "Required for A/B mode.",
    );
  }
}

/**
 * 处理 file 输入的 change 事件 / Handle a Markdown <input type="file"> change event.
 */
export async function handleFileUpload(slot: Slot, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files && input.files[0];
  if (!file) return;

  if (file.size > MAX_MARKDOWN_BYTES) {
    appendLog(`Plan ${slot} file is too large`);
    if (input) input.value = "";
    setPlanStatus(slot, "error", "too large");
    return;
  }

  state.markdown[slot].filename = normalizeMarkdownFilename(file.name, slot);
  try {
    const content = await file.text();
    state.markdown[slot].content = content;
    markMarkdownPlan(slot);
    appendLog(`loaded Plan ${slot} file: ${state.markdown[slot].filename}`);
  } catch (error) {
    setPlanStatus(slot, "error", "read failed");
    const message = error instanceof Error ? error.message : String(error);
    appendLog(`read Plan ${slot} file failed: ${message}`);
    log.warn("plan file read failed", { slot, message });
  }
}

/**
 * 规范化 Markdown 文件名 / Normalize an uploaded filename to end with .md.
 */
export function normalizeMarkdownFilename(name: string, slot: Slot): string {
  const parts = String(name ?? "").split(/[\\/]/);
  const trimmed = parts[parts.length - 1] ?? "";
  const safe = trimmed || `plan-${slot.toLowerCase()}.md`;
  if (/\.(md|markdown)$/i.test(safe)) return safe;
  return `${safe}.md`;
}

/**
 * 清空 Markdown 输入与存储 / Clear the Markdown input and state for a slot.
 */
export function clearMarkdownInput(slot: Slot): void {
  state.markdown[slot].content = "";
  const input = $maybe<HTMLInputElement>(slot === "A" ? "file-a" : "file-b");
  if (input) input.value = "";
}

/**
 * 写入 Markdown 草稿并切换该插槽为 Markdown 来源。
 * Write a Markdown draft and mark the slot as Markdown-backed.
 */
export function setMarkdownPlan(slot: Slot, filename: string, content: string): void {
  state.markdown[slot].filename = normalizeMarkdownFilename(filename, slot);
  state.markdown[slot].content = content;
  if (slot === "A") {
    state.planA = null;
  } else {
    state.planB = null;
  }
  markMarkdownPlan(slot);
}

/**
 * 清空指定 Plan 插槽 / Clear one Plan slot.
 */
export function clearPlanSlot(slot: Slot): void {
  clearMarkdownInput(slot);
  if (slot === "A") {
    state.planA = null;
  } else {
    state.planB = null;
  }
  markMarkdownPlan(slot);
}

/**
 * 从 state.markdown 或 demo catalog 回查 ref 对应的原文 Markdown。
 * 独立实现以避开与 demo-catalog 之间的静态循环依赖；demo-catalog 导出同名函数供 UI 层调用。
 * Lookup raw Markdown for a PlanRef. Implemented locally to avoid a static cycle
 * with demo-catalog; the same name is re-exported from demo-catalog for UI layers.
 */
export function getMarkdownForPlanRef(ref: PlanRef): string {
  return getMarkdownForRef(ref);
}

// 根据 ref 决定定位到 A 还是 B 插槽 / Infer the slot from a PlanRef.
function inferSlot(ref: PlanRef): Slot {
  if (ref.source === "demo") {
    const slot = String(ref.slot ?? "").toLowerCase();
    if (slot.endsWith("b") || slot === "plan_b") return "B";
    return "A";
  }
  if (ref.filename && /(^|[-_])b\./i.test(ref.filename)) return "B";
  return "A";
}

// 调用点私有转发：避免 plan-panel 与 demo-catalog 的直接耦合
// Private trampoline to avoid static circular import with demo-catalog.
function getMarkdownForRef(ref: PlanRef): string {
  if (ref.source === "markdown") return ref.content ?? "";
  if (ref.demo_kind === "single") {
    const demo = state.demos.single.find((d) => d.id === ref.demo_id);
    return demo?.plan_markdown ?? "";
  }
  const demo = state.demos.ab.find((d) => d.id === ref.demo_id);
  if (!demo) return "";
  return String(ref.slot ?? "").toLowerCase().endsWith("b")
    ? demo.plan_b_markdown
    : demo.plan_a_markdown;
}

/**
 * 解析当前 slot 应使用的 PlanRef / Resolve the PlanRef for a given slot.
 * 优先使用上传的 Markdown，否则退回到 state 中保存的 demo 引用。
 * Prefer uploaded Markdown; fall back to the demo reference stored in state.
 */
export function resolvePlanRef(slot: Slot): PlanRef {
  const md = state.markdown[slot].content.trim();
  if (md) {
    return {
      source: "markdown",
      filename: state.markdown[slot].filename,
      content: state.markdown[slot].content,
    };
  }
  const selected = slot === "A" ? state.planA : state.planB;
  if (selected) return selected;
  throw new Error(`Plan ${slot} is empty`);
}

/**
 * 初始化 Plan 面板（绑定 file 输入 + 渲染空态）
 * Bootstrap: bind file-input change handlers and render empty states.
 */
export function initPlanPanel(): void {
  const fa = $maybe<HTMLInputElement>("file-a");
  const fb = $maybe<HTMLInputElement>("file-b");
  fa?.addEventListener("change", (event) => {
    void handleFileUpload("A", event);
  });
  fb?.addEventListener("change", (event) => {
    void handleFileUpload("B", event);
  });
  renderPlanEmpty("A", "Waiting for Markdown or a built-in demo.");
  renderPlanEmpty("B", "Required for A/B mode.");
}
